<script setup lang="ts">
import { useControlId } from '../../composables/useControlId'
import { computed, provide, ref } from 'vue'
import RrRadio, { type RadioGroupContext, type RadioValueType, type RadioChangeEvent } from './RrRadio.vue'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'
export interface RadioOption { label: string; value: RadioValueType; disabled?: boolean }
const props = withDefaults(defineProps<{
  value?: RadioValueType | null
  defaultValue?: RadioValueType
  options?: (string | number | RadioOption)[]
  disabled?: boolean
  name?: string
  size?: 'small' | 'middle' | 'large'
  direction?: 'horizontal' | 'vertical'
}>(), { options: () => [], disabled: undefined, direction: 'horizontal' })
const emit = defineEmits<{ 'update:value': [value: RadioValueType | null | undefined]; change: [event: RadioChangeEvent] }>()
const { disabled, onFieldChange } = useFormControl(props)
const groupId = useControlId()
const innerValue = ref<RadioValueType | null | undefined>(props.defaultValue)
const value = useBoundValue(props, 'value', innerValue)
const name = computed(() => props.name ?? `rr-radio-${groupId}`)
const normalizedOptions = computed<RadioOption[]>(() => props.options.map(option => typeof option === 'object' ? option : { label: String(option), value: option }))
function handleChange(next: RadioValueType | null | undefined, event: RadioChangeEvent) {
  if (next === value.value) return
  innerValue.value = next
  emit('update:value', next)
  emit('change', event)
  return onFieldChange()
}
provide<RadioGroupContext>('rrRadioGroupContext', { value, disabled, name, onChange: handleChange })
</script>

<template>
  <div
    class="rr-radio-group"
    :class="[
      `rr-radio-group--${direction}`,
      { 'is-disabled': disabled }
    ]"
    role="radiogroup"
  >
    <template v-if="normalizedOptions.length > 0">
      <RrRadio
        v-for="opt in normalizedOptions"
        :key="String(opt.value)"
        :value="opt.value"
        :disabled="disabled || opt.disabled"
      >
        {{ opt.label }}
      </RrRadio>
    </template>
    <slot v-else />
  </div>
</template>

<style scoped>
.rr-radio-group {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--rr-space-4, 16px);
  align-items: center;
  line-height: 1;
}

.rr-radio-group--vertical {
  flex-direction: column;
  align-items: flex-start;
  gap: var(--rr-space-2, 8px);
}
</style>
